const RGB_STYLING_ANCHORS = Object.freeze({
  eyewear: { x: 460 / 900, y: 180 / 1350, width: (126 / 900) * 1.35, offsetX: 126 / 900, offsetY: 186 / 1350, baseX: 49, baseY: 16, baseWidth: 13, baseScale: 80 },
  tops: { x: .5, y: .5, width: 1, offsetX: 1, offsetY: 1, baseX: 50, baseY: 52, baseWidth: 55, baseScale: 100 },
  outerwear: { x: .5, y: .5, width: 1.08, offsetX: 1, offsetY: 1, baseX: 50, baseY: 50, baseWidth: 45, baseScale: 100 },
  bottoms: { x: .5, y: .56, width: 1, offsetX: 1, offsetY: 1, baseX: 50, baseY: 50, baseWidth: 45, baseScale: 100 },
  footwear: { x: .5, y: .5, width: 1, offsetX: 1, offsetY: 1, baseX: 50, baseY: 50, baseWidth: 45, baseScale: 100 },
  accessories: { x: .78, y: .48, width: .42, offsetX: .6, offsetY: .7, baseX: 50, baseY: 50, baseWidth: 45, baseScale: 100 }
});

const RGB_STYLING_LAYER_VERSION = 4;
const RGB_STYLING_LAYER_BASES = Object.freeze({
  footwear: 50,
  bottoms: 100,
  tops: 200,
  outerwear: 300,
  accessories: 400,
  eyewear: 500,
  headwear: 600
});

class RGBStylingRoom {
  constructor(root) {
    this.root = root;
    this.dialog = root.querySelector('[data-rgb-styling-dialog]');
    this.review = root.querySelector('[data-rgb-review-dialog]');
    this.products = [...root.querySelectorAll('[data-rgb-styling-product]')];
    this.productsNode = root.querySelector('[data-rgb-products]');
    this.composition = root.querySelector('[data-rgb-composition]');
    this.figureStage = root.querySelector('[data-rgb-figure-stage]');
    this.statusNode = root.querySelector('[data-rgb-system-status]');
    this.lastStatus = this.statusNode?.textContent || '';
    this.reviewList = root.querySelector('[data-rgb-review-list]');
    this.selected = new Map();
    this.opener = null;
    this.reviewOpener = null;
    this.historyActive = false;
    this.statusTimer = 0;
    this.categoryTimer = 0;
    this.scanTimer = 0;
    this.abort = new AbortController();
    this.figureImage = root.querySelector('.rgb-styling__figure img');
    this.figureVisual = this.figureImage || root.querySelector('.rgb-styling__mannequin');
    this.resizeObserver = new ResizeObserver(this.refreshAnchors);
    const figureStage = root.querySelector('.rgb-styling__figure-stage');
    if (figureStage) this.resizeObserver.observe(figureStage);
    this.figureImage?.addEventListener('load', this.refreshAnchors, { signal: this.abort.signal });
    this.bind();
    document.querySelectorAll('[data-rgb-styling-entry]').forEach((button) => {
      if (this.dialog?.id) button.setAttribute('aria-controls', this.dialog.id);
      button.setAttribute('aria-expanded', 'false');
    });
    addEventListener('popstate', this.onPopState, { signal: this.abort.signal });
    window.RGBPreservePreviewLinks?.(this.root);
  }

  bind() {
    const signal = this.abort.signal;
    document.querySelectorAll('[data-rgb-styling-entry]').forEach((button) => button.addEventListener('click', this.open, { signal }));
    this.root.querySelector('[data-rgb-styling-close]')?.addEventListener('click', this.close, { signal });
    this.root.querySelectorAll('[data-rgb-category]').forEach((button) => button.addEventListener('click', this.changeCategory, { signal }));
    this.products.forEach((product) => product.querySelector('[data-rgb-select-product]')?.addEventListener('click', () => this.toggle(product), { signal }));
    this.root.querySelector('[data-rgb-review-open]')?.addEventListener('click', this.openReview, { signal });
    this.root.querySelector('[data-rgb-review-close]')?.addEventListener('click', this.closeReview, { signal });
    this.root.querySelector('[data-rgb-add-outfit]')?.addEventListener('click', this.addOutfit, { signal });
    this.dialog?.addEventListener('cancel', this.onCancel, { signal });
    this.dialog?.addEventListener('keydown', this.onKeydown, { signal });
    this.root.addEventListener('click', this.onDelegatedClick, { signal });
    this.root.addEventListener('change', this.updateReview, { signal });
  }

  open = (event) => {
    this.openForProduct({ opener: event?.currentTarget instanceof HTMLElement ? event.currentTarget : null });
  };

  openForProduct({ opener = null, productId = '', category = 'eyewear' } = {}) {
    if (!this.dialog) return;
    this.opener = opener;
    this.dialog.showModal();
    document.documentElement.classList.add('rgb-styling-open');
    document.querySelectorAll('[data-rgb-styling-entry]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    const categoryButton = this.root.querySelector(`[data-rgb-category="${CSS.escape(category)}"]`);
    if (categoryButton instanceof HTMLElement) {
      this.root.querySelectorAll('[data-rgb-category]').forEach((button) => button.setAttribute('aria-pressed', String(button === categoryButton)));
      this.products.forEach((product) => { product.hidden = product.dataset.category !== category; });
      this.updateCategoryState(category);
    }
    const mapping = productId ? this.products.find((product) => product.dataset.productId === String(productId) && product.dataset.category === category) : null;
    if (mapping && !this.selected.has(mapping.dataset.blockId)) this.toggle(mapping);
    if (!this.historyActive) {
      history.pushState({ ...(history.state || {}), rgbStylingRoom: true }, '', location.href);
      this.historyActive = true;
    }
    requestAnimationFrame(() => this.root.querySelector('[data-rgb-styling-close]')?.focus());
  }

  close = () => {
    if (this.historyActive && history.state?.rgbStylingRoom) {
      history.back();
      return;
    }
    this.finishClose();
  };

  onPopState = () => {
    if (!this.historyActive) return;
    this.historyActive = false;
    this.finishClose();
  };

  finishClose() {
    if (this.review && !this.review.hidden) this.review.hidden = true;
    if (this.dialog?.open) this.dialog.close();
    document.documentElement.classList.remove('rgb-styling-open');
    document.querySelectorAll('[data-rgb-styling-entry]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    document.dispatchEvent(new CustomEvent('rgb:styling-close'));
    this.opener?.focus();
  }

  changeCategory = (event) => {
    const category = event.currentTarget.dataset.rgbCategory;
    this.root.querySelectorAll('[data-rgb-category]').forEach((button) => button.setAttribute('aria-pressed', String(button === event.currentTarget)));
    this.products.forEach((product) => { product.hidden = product.dataset.category !== category; });
    event.currentTarget.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
    this.updateCategoryState(category);
    this.animateCategoryChange();
    this.setStatus(`${category.toUpperCase()} LOADED`);
  };

  toggle(product) {
    const key = product.dataset.blockId;
    if (!key || !product.dataset.productId) return;
    if (this.selected.has(key)) return this.remove(key);
    if (product.dataset.multiple !== 'true') {
      [...this.selected.entries()].filter(([, item]) => item.dataset.category === product.dataset.category).forEach(([id]) => this.remove(id, false));
    }
    this.selected.set(key, product);
    product.querySelector('[data-rgb-select-product]')?.setAttribute('aria-pressed', 'true');
    this.addVisual(product);
    if (matchMedia('(max-width: 749px)').matches) {
      this.root.querySelector('.rgb-styling__workspace')?.scrollTo({ top: 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    this.announce();
    this.setStatus(`${product.dataset.category.toUpperCase()} ASSIGNED`);
  }

  addVisual(product) {
    const source = product.querySelector('img');
    if (!source || !this.composition) return;
    const visual = document.createElement('div');
    visual.className = 'rgb-styling__selected';
    visual.dataset.selectedBlock = product.dataset.blockId;
    visual.dataset.category = product.dataset.category;
    const configured = {
      x: Number(product.dataset.x), y: Number(product.dataset.y), width: Number(product.dataset.width),
      rotation: Number(product.dataset.rotation), z: Number(product.dataset.z), scale: Number(product.dataset.scale)
    };
    const untouched = configured.x === 50 && configured.y === 50 && configured.width === 45 && configured.rotation === 0 && configured.z === 10 && configured.scale === 100;
    const defaults = {
      eyewear: { x: 50, y: 19, width: 28, scale: 0.85 }, headwear: { x: 50, y: 10, width: 34, scale: 0.9 },
      tops: { x: 50, y: 42, width: 48, scale: 1 }, outerwear: { x: 50, y: 44, width: 58, scale: 1 },
      bottoms: { x: 50, y: 69, width: 48, scale: 1 }, footwear: { x: 50, y: 91, width: 42, scale: 1 },
      accessories: { x: 76, y: 43, width: 32, scale: 1 }
    };
    const placement = untouched ? defaults[product.dataset.category] : null;
    const categoryLayer = RGB_STYLING_LAYER_BASES[product.dataset.category] || 0;
    visual.style.cssText = `--sx:${placement?.x ?? configured.x}%;--sy:${placement?.y ?? configured.y}%;--sw:${placement?.width ?? configured.width}%;--sr:${configured.rotation}deg;--sz:${categoryLayer + configured.z};--ss:${placement?.scale ?? configured.scale / 100}`;
    visual.innerHTML = `<img src="${source.currentSrc || source.src}" alt="">`;
    const removeZone = document.createElement('div');
    removeZone.className = 'rgb-styling__remove-zone';
    removeZone.style.zIndex = '1000';
    removeZone.dataset.selectedBlock = product.dataset.blockId;
    removeZone.innerHTML = `<button type="button" data-rgb-remove-visual aria-label="Remove ${product.dataset.title || 'product'}">×</button>`;
    this.composition.append(visual, removeZone);
    this.positionVisual(visual, product);
    this.triggerObjectScan();
  }

  positionVisual(visual, product) {
    const category = product.dataset.category;
    if ((!RGB_STYLING_ANCHORS[category] && category !== 'headwear') || !this.figureVisual || !this.composition) return;
    const figure = this.figureVisual.getBoundingClientRect();
    const stage = this.composition.getBoundingClientRect();
    if (!figure.width || !figure.height || !stage.width || !stage.height) return;
    const headWidth = figure.width * (96 / 900);
    const headHeight = figure.height * (186 / 1350);
    let x;
    let y;
    let width;

    if (category !== 'headwear') {
      const anchor = RGB_STYLING_ANCHORS[category];
      const configured = {
        x: Number(product.dataset.x) || anchor.baseX,
        y: Number(product.dataset.y) || anchor.baseY,
        width: Number(product.dataset.width) || anchor.baseWidth,
        scale: Number(product.dataset.scale) || anchor.baseScale
      };
      x = figure.left - stage.left + figure.width * anchor.x + ((configured.x - anchor.baseX) / 100) * figure.width * anchor.offsetX;
      y = figure.top - stage.top + figure.height * anchor.y + ((configured.y - anchor.baseY) / 100) * figure.height * anchor.offsetY;
      width = figure.width * anchor.width * (configured.width / anchor.baseWidth) * (configured.scale / anchor.baseScale);
    } else {
      const configured = {
        x: Number(product.dataset.x) || 50,
        y: Number(product.dataset.y) || 50,
        width: Number(product.dataset.width) || 45,
        scale: Number(product.dataset.scale) || 100
      };
      const headTop = figure.top - stage.top + figure.height * (24 / 1350);
      width = headWidth * 2.1 * (configured.width / 45) * (configured.scale / 100);
      x = figure.left - stage.left + figure.width * (460 / 900) + ((configured.x - 50) / 100) * headWidth;
      y = headTop - 4 + width * 0.303 + ((configured.y - 50) / 100) * headHeight;
    }

    visual.dataset.anchor = category;
    visual.style.setProperty('--sx', `${x}px`);
    visual.style.setProperty('--sy', `${y}px`);
    visual.style.setProperty('--sw', `${width}px`);
    visual.style.setProperty('--ss', '1');
  }

  refreshAnchors = () => {
    this.composition?.querySelectorAll('.rgb-styling__selected[data-anchor]').forEach((visual) => {
      const product = this.products.find((item) => item.dataset.blockId === visual.dataset.selectedBlock);
      if (product) this.positionVisual(visual, product);
    });
  };

  remove(key, reportStatus = true) {
    const product = this.selected.get(key);
    product?.querySelector('[data-rgb-select-product]')?.setAttribute('aria-pressed', 'false');
    this.selected.delete(key);
    this.composition?.querySelectorAll(`[data-selected-block="${CSS.escape(key)}"]`).forEach((node) => node.remove());
    this.announce();
    if (reportStatus) this.setStatus(this.selected.size === 0 ? 'FIT CLEARED' : 'OBJECT REMOVED');
    if (this.review && !this.review.hidden) {
      if (this.selected.size === 0) this.closeReview();
      else this.renderReview();
    }
  }

  announce() {
    const count = this.selected.size;
    this.root.querySelectorAll('[data-rgb-selection-count]').forEach((node) => { node.textContent = `${count} ITEM${count === 1 ? '' : 'S'}`; });
    const reviewCount = this.root.querySelector('[data-rgb-review-count]');
    if (reviewCount) reviewCount.textContent = `(${count})`;
    const category = this.root.querySelector('[data-rgb-category][aria-pressed="true"]')?.dataset.rgbCategory;
    this.updateCategoryState(category);
  }

  updateCategoryState(category) {
    if (!category) return;
    const hasSelected = [...this.selected.values()].some((product) => product.dataset.category === category);
    this.products.forEach((product) => {
      if (product.dataset.category === category && !product.dataset.productId) product.hidden = hasSelected;
    });
    this.root.querySelector('[data-rgb-products]')?.classList.toggle('has-category-selection', hasSelected);
  }

  setStatus(message) {
    if (!message || !this.statusNode || message === this.lastStatus) return;
    this.lastStatus = message;
    this.statusNode.textContent = message;
    const region = this.statusNode.closest('.rgb-styling__status');
    if (!region || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    clearTimeout(this.statusTimer);
    region.classList.remove('is-updating');
    void region.offsetWidth;
    region.classList.add('is-updating');
    this.statusTimer = window.setTimeout(() => region.classList.remove('is-updating'), 180);
  }

  animateCategoryChange() {
    if (!this.productsNode || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    clearTimeout(this.categoryTimer);
    this.productsNode.classList.remove('is-category-switching');
    void this.productsNode.offsetWidth;
    this.productsNode.classList.add('is-category-switching');
    this.categoryTimer = window.setTimeout(() => this.productsNode?.classList.remove('is-category-switching'), 260);
  }

  triggerObjectScan() {
    if (!this.figureStage || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    clearTimeout(this.scanTimer);
    this.figureStage.classList.remove('is-object-scanning');
    void this.figureStage.offsetWidth;
    this.figureStage.classList.add('is-object-scanning');
    this.scanTimer = window.setTimeout(() => this.figureStage?.classList.remove('is-object-scanning'), 340);
  }

  openReview = (event) => {
    if (!this.review || this.selected.size === 0) return;
    this.reviewOpener = event?.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    this.review.hidden = false;
    this.renderReview();
    this.root.querySelector('[data-rgb-review-close]')?.focus();
  };

  closeReview = () => {
    if (!this.review || this.review.hidden) return;
    this.review.hidden = true;
    this.reviewOpener?.focus();
  };

  renderReview() {
    if (!this.reviewList) return;
    this.reviewList.replaceChildren();
    this.selected.forEach((product) => {
      const template = product.querySelector('[data-rgb-review-template]');
      if (template instanceof HTMLTemplateElement) this.reviewList.append(template.content.cloneNode(true));
    });
    window.RGBPreservePreviewLinks?.(this.reviewList);
    this.updateReview();
  }

  updateReview = () => {
    let subtotal = 0;
    let available = 0;
    this.reviewList?.querySelectorAll('[data-rgb-review-item]').forEach((item) => {
      const select = item.querySelector('[data-rgb-review-variant]');
      const quantity = item.querySelector('[data-rgb-review-quantity]');
      const option = select?.selectedOptions[0];
      const qty = Math.max(1, Number(quantity?.value) || 1);
      const isAvailable = option?.dataset.available === 'true';
      const line = Number(option?.dataset.price || 0) * qty;
      if (isAvailable) { subtotal += line; available += 1; }
      const price = item.querySelector('[data-rgb-review-price]');
      const status = item.querySelector('[data-rgb-review-availability]');
      if (price) price.textContent = isAvailable ? `${option.dataset.priceLabel} × ${qty}` : 'SOLD OUT';
      if (status) status.textContent = isAvailable ? 'AVAILABLE' : 'SOLD OUT';
    });
    const subtotalNode = this.root.querySelector('[data-rgb-subtotal]');
    if (subtotalNode) subtotalNode.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: Shopify.currency?.active || 'USD' }).format(subtotal / 100);
    const button = this.root.querySelector('[data-rgb-add-outfit]');
    if (button instanceof HTMLButtonElement) button.disabled = available === 0;
  };

  onDelegatedClick = (event) => {
    const visual = event.target.closest('[data-rgb-remove-visual]')?.closest('[data-selected-block]');
    if (visual) this.remove(visual.dataset.selectedBlock);
    const reviewItem = event.target.closest('[data-rgb-review-remove]')?.closest('[data-rgb-review-item]');
    if (reviewItem) this.remove(reviewItem.dataset.blockId);
  };

  addOutfit = async (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;
    const { CartLinesUpdateEvent } = await import('@shopify/events');
    const status = this.root.querySelector('[data-rgb-cart-status]');
    const items = [...(this.reviewList?.querySelectorAll('[data-rgb-review-item]') || [])].map((item) => {
      const option = item.querySelector('[data-rgb-review-variant]')?.selectedOptions[0];
      return option?.dataset.available === 'true' ? { id: Number(option.value), quantity: Math.max(1, Number(item.querySelector('[data-rgb-review-quantity]')?.value) || 1) } : null;
    }).filter(Boolean);
    if (!items.length) return;
    button.disabled = true;
    if (status) status.textContent = 'ADDING TO CART…';
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((item) => item.dataset.sectionId).filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.root.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    }));
    try {
      const response = await fetch(Theme.routes.cart_add_url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items, sections: sectionIds.join(','), sections_url: window.location.pathname }) });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || 'Unable to add selection');
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((result) => result.json());
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { sections: data.sections, items: cart.items, itemCount: cart.item_count, source: 'rgb-styling-room', didError: false } });
      if (status) status.textContent = 'SELECTION ADDED TO CART';
    } catch (error) {
      deferred.reject(error);
      if (status) status.textContent = error instanceof Error ? error.message : 'Unable to add selection';
    } finally { button.disabled = false; }
  };

  onCancel = (event) => { event.preventDefault(); this.close(); };
  onKeydown = (event) => {
    if (event.key === 'Escape' && (!this.review || this.review.hidden)) {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }
    if (!this.review || this.review.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeReview();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...this.review.querySelectorAll('button:not([disabled]), a[href], select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((item) => item instanceof HTMLElement && !item.hidden);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  destroy() {
    clearTimeout(this.statusTimer);
    clearTimeout(this.categoryTimer);
    clearTimeout(this.scanTimer);
    this.abort.abort();
    this.resizeObserver.disconnect();
    if (this.review) this.review.hidden = true;
    if (this.dialog?.open) this.dialog.close();
    document.documentElement.classList.remove('rgb-styling-open');
  }
}

function initRGBStyling(scope = document) {
  scope.querySelectorAll('[data-rgb-styling-room]').forEach((root) => {
    if (!root.rgbStylingRoom) root.rgbStylingRoom = new RGBStylingRoom(root);
  });
}

window.RGBStylingRoomInit = initRGBStyling;
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initRGBStyling(), { once: true });
} else {
  queueMicrotask(() => initRGBStyling());
}
document.addEventListener('shopify:section:load', (event) => initRGBStyling(event.target));
document.addEventListener('shopify:section:unload', (event) => event.target.querySelector('[data-rgb-styling-room]')?.rgbStylingRoom?.destroy());
